/**
 * The 18-language list and flag artwork, ported verbatim from app/app.js
 * (LANGS / FLAG_SVG are lexical there, unreachable from React) and pinned
 * against the oracle source by languages.test.ts. `edition` is the
 * quran.com translation id; null means Arabic (no translation line).
 */
export interface AppLanguage {
  code: string;
  cc: string;
  name: string;
  edition: number | null;
}

export const LANGS: readonly AppLanguage[] = [
  { code: 'en', cc: 'GB', name: 'English', edition: 20 },
  { code: 'ar', cc: 'SA', name: 'العربية', edition: null },
  { code: 'ur', cc: 'PK', name: 'اردو', edition: 234 },
  { code: 'id', cc: 'ID', name: 'Bahasa Indonesia', edition: 33 },
  { code: 'tr', cc: 'TR', name: 'Türkçe', edition: 77 },
  { code: 'fr', cc: 'FR', name: 'Français', edition: 136 },
  { code: 'es', cc: 'ES', name: 'Español', edition: 83 },
  { code: 'bn', cc: 'BD', name: 'বাংলা', edition: 161 },
  { code: 'hi', cc: 'IN', name: 'हिन्दी', edition: 122 },
  { code: 'ms', cc: 'MY', name: 'Bahasa Melayu', edition: 39 },
  { code: 'de', cc: 'DE', name: 'Deutsch', edition: 27 },
  { code: 'ru', cc: 'RU', name: 'Русский', edition: 45 },
  { code: 'zh', cc: 'CN', name: '中文', edition: 109 },
  { code: 'fa', cc: 'IR', name: 'فارسی', edition: 135 },
  { code: 'sw', cc: 'KE', name: 'Kiswahili', edition: 49 },
  { code: 'pt', cc: 'PT', name: 'Português', edition: 43 },
  { code: 'ha', cc: 'NG', name: 'Hausa', edition: 32 },
  { code: 'so', cc: 'SO', name: 'Soomaali', edition: 46 },
];

/* Windows renders flag emoji as plain "GB"-style letter pairs (no colour flag glyphs in
   its system font), so the language picker draws its own small flag SVGs instead —
   these render identically on every platform, including the offline artifact build. */
export const FLAG_SVG: Readonly<Record<string, string>> = {
  GB:'<rect width="30" height="20" fill="#0A2A6B"/><path d="M0 0 30 20M30 0 0 20" stroke="#fff" stroke-width="4"/><path d="M0 0 30 20M30 0 0 20" stroke="#C8102E" stroke-width="1.6"/><path d="M15 0V20M0 10H30" stroke="#fff" stroke-width="6.5"/><path d="M15 0V20M0 10H30" stroke="#C8102E" stroke-width="3.8"/>',
  SA:'<image href="img/flags/sa.svg" width="30" height="20" preserveAspectRatio="none"/>',
  PK:'<rect width="30" height="20" fill="#0F5132"/><rect width="7.5" height="20" fill="#fff"/><circle cx="19" cy="10" r="4.6" fill="#fff"/><circle cx="20.6" cy="9" r="4.2" fill="#0F5132"/><path d="M23.6 6.6 24.4 9.2 27 9.2 24.9 10.7 25.7 13.2 23.6 11.7 21.5 13.2 22.3 10.7 20.2 9.2 22.8 9.2Z" fill="#fff"/>',
  ID:'<rect width="30" height="10" fill="#CE1126"/><rect y="10" width="30" height="10" fill="#fff"/>',
  TR:'<rect width="30" height="20" fill="#E30A17"/><circle cx="12.5" cy="10" r="5" fill="#fff"/><circle cx="14" cy="10" r="4" fill="#E30A17"/><path d="M17 6.4 17.9 9.2 20.8 9.2 18.5 10.9 19.3 13.7 17 12 14.7 13.7 15.5 10.9 13.2 9.2 16.1 9.2Z" fill="#fff"/>',
  FR:'<rect width="10" height="20" fill="#0055A4"/><rect x="10" width="10" height="20" fill="#fff"/><rect x="20" width="10" height="20" fill="#EF4135"/>',
  ES:'<rect width="30" height="20" fill="#AA151B"/><rect y="5" width="30" height="10" fill="#F1BF00"/>',
  BD:'<rect width="30" height="20" fill="#006A4E"/><circle cx="13" cy="10" r="6" fill="#F42A41"/>',
  IN:'<rect width="30" height="6.66" fill="#FF9933"/><rect y="6.66" width="30" height="6.68" fill="#fff"/><rect y="13.34" width="30" height="6.66" fill="#138808"/><circle cx="15" cy="10" r="2.6" fill="none" stroke="#000080" stroke-width=".7"/>',
  MY:'<image href="img/flags/my.svg" width="30" height="20" preserveAspectRatio="none"/>',
  DE:'<rect width="30" height="6.66" fill="#000"/><rect y="6.66" width="30" height="6.68" fill="#DD0000"/><rect y="13.34" width="30" height="6.66" fill="#FFCE00"/>',
  RU:'<rect width="30" height="6.66" fill="#fff"/><rect y="6.66" width="30" height="6.68" fill="#0039A6"/><rect y="13.34" width="30" height="6.66" fill="#D52B1E"/>',
  CN:'<rect width="30" height="20" fill="#DE2910"/><path d="M6 4 6.9 6.8 9.8 6.8 7.5 8.5 8.4 11.3 6 9.6 3.6 11.3 4.5 8.5 2.2 6.8 5.1 6.8Z" fill="#FFDE00"/>',
  IR:'<rect width="30" height="6.66" fill="#239F40"/><rect y="6.66" width="30" height="6.68" fill="#fff"/><rect y="13.34" width="30" height="6.66" fill="#DA0000"/>',
  KE:'<rect width="30" height="20" fill="#fff"/><rect y="2" width="30" height="4.5" fill="#000"/><rect y="8.75" width="30" height="2.5" fill="#BB0000"/><rect y="13.5" width="30" height="4.5" fill="#006600"/>',
  PT:'<rect width="12" height="20" fill="#006600"/><rect x="12" width="18" height="20" fill="#FF0000"/><circle cx="12" cy="10" r="3.4" fill="#FFCC00" stroke="#fff" stroke-width=".6"/>',
  NG:'<rect width="10" height="20" fill="#008751"/><rect x="10" width="10" height="20" fill="#fff"/><rect x="20" width="10" height="20" fill="#008751"/>',
  SO:'<rect width="30" height="20" fill="#4189DD"/><path d="M15 5.4 16 8.6 19.4 8.6 16.7 10.6 17.7 13.8 15 11.8 12.3 13.8 13.3 10.6 10.6 8.6 14 8.6Z" fill="#fff"/>',
};

export function flagSvg(cc: string): string {
  return '<svg class="flagsvg" viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' + (FLAG_SVG[cc] || '<rect width="30" height="20" fill="#999"/>') + '</svg>';
}

/** The language behind a stored code, defaulting to English like the legacy screens. */
export function languageForCode(code: string): AppLanguage {
  return LANGS.find((lang) => lang.code === code) || LANGS[0];
}
